import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import JoinCodeDialog from "../components/JoinCodeDialog";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useAuth } from "../context/AuthContext";
import { useAnalytics } from "../context/AnalyticsContext";
import { AppButton } from "../components/ui";
import ExperiencesAPI from "../services/ExperiencesAPI";
import "./Viewer.scss";

const VIEWER_BASE_URL = process.env.REACT_APP_VIEWER_BASE_URL || "https://viewer.10k.science";
const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const Viewer = () => {
  const navigate = useNavigate();
  const { experienceId: experienceIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isJoinCodeDialogOpen, setIsJoinCodeDialogOpen] = useState(false);
  const [experienceName, setExperienceName] = useState("");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { userName, userId, organizationId, subscriptionStatus } = useAuth();
  const { markAnalyticsDirty, scheduleAnalyticsRefresh } = useAnalytics();

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowTitleInHeader(false);
    setTitle("Viewer");
  }, [setShowTitleInHeader, setTitle]);

  useEffect(() => {
    markAnalyticsDirty();

    return () => {
      markAnalyticsDirty();
      scheduleAnalyticsRefresh({ delayMs: 4000 });
    };
  }, [markAnalyticsDirty, scheduleAnalyticsRefresh]);

  const experienceIdFromQuery = searchParams.get("experienceId");

  const experienceId = useMemo(
    () => experienceIdFromPath || experienceIdFromQuery || "",
    [experienceIdFromPath, experienceIdFromQuery]
  );

  const embedUrl = useMemo(() => {
    if (!experienceId) {
      return "";
    }

    const viewerParams = new URLSearchParams({
      userName: userName || "",
      userId: userId || "",
      organizationId: organizationId || "",
      subscriptionStatus: subscriptionStatus || "",
    });

    console.log(`${VIEWER_BASE_URL}/experiences/embed/${encodeURIComponent(experienceId)}?${viewerParams.toString()}`);
    return `${VIEWER_BASE_URL}/experiences/embed/${encodeURIComponent(experienceId)}?${viewerParams.toString()}`;
  }, [experienceId, userName, userId, organizationId, subscriptionStatus]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [embedUrl]);

  const openExitDialog = useCallback(() => {
    setIsExitDialogOpen(true);
  }, []);

  const openJoinCodeDialog = useCallback(() => {
    setIsJoinCodeDialogOpen(true);
  }, []);

  const closeExitDialog = useCallback(() => {
    setIsExitDialogOpen(false);
  }, []);

  const closeJoinCodeDialog = useCallback(() => {
    setIsJoinCodeDialogOpen(false);
  }, []);

  const handleConfirmBackToHome = useCallback(() => {
    setIsExitDialogOpen(false);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    if (!experienceId) {
      setExperienceName("");
      return undefined;
    }

    const loadExperienceName = async () => {
      try {
        const response = await ExperiencesAPI.get(`experiences/${experienceId}`, {
          requiresAuth: true,
        });
        const experience = getApiResponseData(response);

        if (!cancelled) {
          setExperienceName(
            experience?.name || experience?.title || experience?.metadata?.name || ""
          );
        }
      } catch (error) {
        console.error("Failed to load experience details for join codes", error);
        if (!cancelled) {
          setExperienceName("");
        }
      }
    };

    loadExperienceName();

    return () => {
      cancelled = true;
    };
  }, [experienceId]);

  return (
    <main className="viewerPage">
      <section className="viewerPage__content">
        <div className="viewerPage__overlayControls">
          <AppButton
            className="viewerPage__overlayButton viewerPage__backButton"
            kind="secondary"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={openExitDialog}
          >
            Back to Home
          </AppButton>
          <AppButton
            className="viewerPage__overlayButton viewerPage__joinButton"
            kind="secondary"
            onClick={openJoinCodeDialog}
            disabled={!experienceId}
          >
            Join Code
          </AppButton>
        </div>

        {!embedUrl && (
          <div className="viewerPage__status">
            Select an experience from Home to launch the embedded viewer.
          </div>
        )}

        {embedUrl && (
          <div className="viewerPage__embed">
            {!iframeLoaded && (
              <div className="viewerPage__loader">
                <div className="viewerPage__loaderSpinner" />
                <p className="viewerPage__loaderText">Loading experience…</p>
              </div>
            )}
            <iframe
              src={embedUrl}
              title="10k Web Viewer"
              loading="lazy"
              allow="camera; microphone; fullscreen"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setIframeLoaded(true)}
              style={{ opacity: iframeLoaded ? 1 : 0, transition: "opacity 400ms ease" }}
            />
          </div>
        )}

        <Dialog
          open={isExitDialogOpen}
          onClose={closeExitDialog}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            className: "viewerExitDialog__paper",
          }}
        >
          <DialogTitle className="viewerExitDialog__title">Quit preview?</DialogTitle>
          <DialogContent className="viewerExitDialog__content">
            Quit previewing the experience and go back home?
          </DialogContent>
          <DialogActions className="viewerExitDialog__actions">
            <AppButton kind="ghost" onClick={closeExitDialog}>
              Keep Previewing
            </AppButton>
            <AppButton kind="primary" onClick={handleConfirmBackToHome}>
              Back to Home
            </AppButton>
          </DialogActions>
        </Dialog>
        <JoinCodeDialog
          open={isJoinCodeDialogOpen}
          onClose={closeJoinCodeDialog}
          experienceId={experienceId}
          experienceName={experienceName}
        />
      </section>
    </main>
  );
};

export default Viewer;
